import Hypher from "hypher";
import fi from "hyphenation.fi";
import usHeads from "./usCompoundHeads.json";

// hyph-fi.tex patterns (Kauko Saarinen, v2.2) — same source as Flutter's
// hyphenator_impure / auto_hyphenating_text packages.
const hyphenator = new Hypher(fi);

// u2s guards -us compounds (avaruus|aika) but also blocks ruu-su, lu-sik-ka.
// Early in the word (s followed by a vowel, no -us word ending there) u-s breaks
// come from a copy of the patterns without u2s. Same rule as Flutter's
// FinnishHyphenator.
function withoutU2s(patterns) {
    const bySize = {};
    for (const [size, packed] of Object.entries(patterns)) {
        bySize[size] = packed.match(new RegExp(`.{1,${size}}`, "g")).filter((p) => p !== "u2s").join("");
    }
    return bySize;
}
const noU2s = new Hypher({ ...fi, patterns: withoutU2s(fi.patterns) });
const US_EARLY_BREAK_MAX = 3;
// Every word of 4 letters or fewer ending in "us" in kotus_sanat/omorfi_sanat (alus|astia, hius|öljy).
const SHORT_US_WORDS = new Set(["alus", "anus", "edus", "haus", "hius", "hus", "idus", "opus", "otus", "plus", "pus", "rus", "sus", "taus", "tus", "zeus"]);

// Patterns can't tell ruus|u from avaruus|aika, so move the break after the s
// when the head is a dictionary -us/-ys word (kauneus, rakennus). usCompoundHeads.json is generated
// from the Flutter app's assets/dictionaries/us_compound_heads.txt.
const usHeadSet = new Set(usHeads);
const VOWELS = "aeiouyäö";
const SOFT_HYPHEN = String.fromCharCode(0xad);

function breakSet(parts) {
    const breaks = new Set();
    let pos = 0;
    for (const part of parts.slice(0, -1)) breaks.add((pos += part.length));
    return breaks;
}

const patternHyphenate = hyphenator.hyphenate.bind(hyphenator);
hyphenator.hyphenate = (word) => {
    const parts = patternHyphenate(word);
    if (word.includes(SOFT_HYPHEN)) return parts;
    const lower = word.toLowerCase();
    const breaks = breakSet(parts);
    let changed = false;
    if (/^.{1,3}us/.test(lower)) {
        for (const i of breakSet(noU2s.hyphenate(word))) {
            if (i <= US_EARLY_BREAK_MAX && lower[i - 1] === "u" && lower[i] === "s" && VOWELS.includes(lower[i + 1] || "-")
                && !SHORT_US_WORDS.has(lower.slice(0, i + 1)) && !breaks.has(i)) {
                breaks.add(i);
                changed = true;
            }
        }
    }
    for (let i = 4; i <= word.length - 2; i++) {
        // -oida/-oitu/-ointi after a loan stem is a verb suffix, not a compound part (fo-ku-soi-da).
        if (VOWELS.includes(lower[i]) && !lower.startsWith("oid", i) && !lower.startsWith("oit", i) && usHeadSet.has(lower.slice(0, i))) {
            breaks.delete(i - 1);
            breaks.add(i);
            changed = true;
        }
    }
    if (!changed) return parts;
    const result = [];
    let last = 0;
    for (const i of [...breaks].sort((a, b) => a - b)) {
        result.push(word.slice(last, i));
        last = i;
    }
    result.push(word.slice(last));
    return result;
};

// Inserts soft hyphens per hyph-fi.tex rules. Hypher always uses U+00AD and
// skips words of 4 chars or fewer (leftmin=2 + rightmin=2).
export function hyphenateFinnish(text) {
    return hyphenator.hyphenateText(text);
}
